import * as z from 'zod'

/**
 * Why an agent turn ended without an answer.
 *
 * The first three are written by the server as a terminal NDJSON frame; the rest are raised by the
 * client when the stream itself is unusable. The response is a streaming Function URL behind
 * CloudFront, where the status code and headers set in the handler do not reliably reach the
 * browser — a refused turn arrives as an empty 200 — so the reason travels in the body, never in
 * the status.
 */
export const agentStreamErrorCodes = [
  'quota_exceeded',
  'no_profile',
  'server_error',
  'empty_response',
  'request_failed',
  'session_expired',
] as const
export type AgentStreamErrorCode = (typeof agentStreamErrorCodes)[number]

/**
 * The one human message per code. Never the server's error text, and never "this month": the quota
 * has no reset (same rule as `generation-failed-copy.ts`).
 */
export const agentStreamErrorMessages: Record<AgentStreamErrorCode, string> = {
  quota_exceeded: "You've used all your AI tokens, so Tunabrain couldn't answer.",
  no_profile: "Your account has no AI token allowance yet, so Tunabrain couldn't answer.",
  server_error: "Tunabrain couldn't answer. Try again.",
  empty_response: "Tunabrain didn't send an answer back. Try again.",
  request_failed: "Tunabrain couldn't be reached. Check your connection and try again.",
  session_expired: 'Your session expired. Sign in again to keep going.',
}

export function isAgentStreamErrorCode(value: unknown): value is AgentStreamErrorCode {
  return typeof value === 'string' && (agentStreamErrorCodes as readonly string[]).includes(value)
}

/**
 * The terminal error frame. Same shape as the Vercel AI SDK `UIMessageChunk` `{ type: 'error', errorText }`
 * the stream already carries, plus a `code` so the client picks its own copy instead of trusting text.
 */
export const agentStreamErrorFrameSchema = z.object({
  type: z.literal('error'),
  errorText: z.string(),
  code: z.enum(agentStreamErrorCodes).describe('Why the turn ended; absent on SDK-emitted error chunks').optional(),
})
export type AgentStreamErrorFrame = z.infer<typeof agentStreamErrorFrameSchema>

export const agentStreamErrorFrame = (code: AgentStreamErrorCode): AgentStreamErrorFrame => ({
  type: 'error',
  errorText: agentStreamErrorMessages[code],
  code,
})

/**
 * The message to show for an error frame. A known `code` wins; a frame without one (an SDK-emitted
 * error chunk, whose text may be internal) or with a code this build does not know gets the generic
 * line.
 */
export const agentStreamErrorMessage = (frame: { code?: unknown }): string =>
  isAgentStreamErrorCode(frame.code) ? agentStreamErrorMessages[frame.code] : agentStreamErrorMessages.server_error
